import axios from 'axios';
import { setPdfPatient, cachePdfPatients } from './utils/fillable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.token) {
        config.headers.Authorization = `Bearer ${userData.token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Remember the chart opened last so generated PDFs carry its Name / DOB / MR# / Dx / Admission line
axiosInstance.interceptors.response.use((response) => {
  if (/\/patients\/[^/?]+$/.test(response.config?.url || '') && response.config?.method === 'get') {
    const p = response.data?.patient || (response.data?.id ? response.data : null);
    if (p) setPdfPatient(p);
  } else if (/\/patients(\?.*)?$/.test(response.config?.url || '') && Array.isArray(response.data?.patients)) {
    cachePdfPatients(response.data.patients);
  }
  return response;
});

// Authentication APIs
export const authAPI = {
  login: async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData) => {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data;
  },
};

// Contact APIs
export const contactAPI = {
  create: async (contactData) => {
    const response = await axiosInstance.post('/contacts', contactData);
    return response.data;
  },
  getAll: async () => {
    const response = await axiosInstance.get('/contacts');
    return response.data;
  },
};

// Patient APIs
export const patientAPI = {
  getAll: async (search = '', status = '') => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    const response = await axiosInstance.get('/patients', { params });
    return response.data;
  },
  create: async (patientData) => {
    const response = await axiosInstance.post('/patients', patientData);
    return response.data;
  },
  getById: async (patientId) => {
    const response = await axiosInstance.get(`/patients/${patientId}`);
    return response.data;
  },
  update: async (patientId, patientData) => {
    const response = await axiosInstance.put(`/patients/${patientId}`, patientData);
    return response.data;
  },
  delete: async (patientId) => {
    const response = await axiosInstance.delete(`/patients/${patientId}`);
    return response.data;
  },
};

// Appointment APIs
export const appointmentAPI = {
  getAll: async (date = '') => {
    const params = {};
    if (date) params.date = date;
    const response = await axiosInstance.get('/appointments', { params });
    return response.data;
  },
  create: async (appointmentData) => {
    const response = await axiosInstance.post('/appointments', appointmentData);
    return response.data;
  },
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: async () => {
    const response = await axiosInstance.get('/dashboard/stats');
    return response.data;
  },
};

export default axiosInstance;
